/**
 * Helpers de formatação da sugestão terapêutica → HTML editável no DocumentEditor.
 * Mantém o fluxo de documentos (content string) sem alterar o contrato da API.
 */

export const CLINICAL_SUGGESTION_DISCLAIMER =
  'Sugestão de apoio. Avaliação e responsabilidade final do médico.';

export interface TherapeuticSuggestionItem {
  medicationId: string;
  medicationLabel: string;
  suggestedDose: string;
  suggestedFrequency: string;
  suggestedDurationDays: number;
  standardInstructions?: string | null;
  administrationRoute?: string;
}

export interface TherapeuticSuggestionPayload {
  diseaseId: string;
  diseaseName: string;
  clinicalCode?: string | null;
  items: TherapeuticSuggestionItem[];
  disclaimer?: string;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function formatDurationDays(days: number): string {
  if (days === 1) return '1 dia';
  return `${days} dias`;
}

/**
 * Gera HTML de pré-preenchimento para colar no editor de receita.
 * Todo o conteúdo permanece editável pelo médico.
 */
export function buildPrescriptionSuggestionHtml(
  payload: TherapeuticSuggestionPayload,
): string {
  const disclaimer = payload.disclaimer || CLINICAL_SUGGESTION_DISCLAIMER;
  const code = payload.clinicalCode
    ? ` (${escapeHtml(payload.clinicalCode)})`
    : '';

  const itemsHtml = payload.items
    .map((item, index) => {
      const instructions = item.standardInstructions
        ? `<br/><em>Instruções:</em> ${escapeHtml(item.standardInstructions)}`
        : '';
      const route = item.administrationRoute
        ? ` — ${escapeHtml(item.administrationRoute)}`
        : '';
      return (
        `<p><strong>${index + 1}. ${escapeHtml(item.medicationLabel)}</strong>${route}<br/>` +
        `<em>Dose:</em> ${escapeHtml(item.suggestedDose)} | ` +
        `<em>Frequência:</em> ${escapeHtml(item.suggestedFrequency)} | ` +
        `<em>Duração:</em> ${escapeHtml(formatDurationDays(item.suggestedDurationDays))}` +
        `${instructions}</p>`
      );
    })
    .join('');

  return (
    `<p><strong>Receituário (rascunho assistido)</strong></p>` +
    `<p><em>Doença selecionada:</em> ${escapeHtml(payload.diseaseName)}${code}</p>` +
    itemsHtml +
    `<p><em>${escapeHtml(disclaimer)}</em></p>` +
    `<p><br/></p>`
  );
}

export function mergeSuggestionIntoContent(
  currentContent: string,
  suggestionHtml: string,
): string {
  const empty =
    !currentContent ||
    currentContent.trim() === '' ||
    currentContent.trim() === '<p><br></p>';
  if (empty) return suggestionHtml;
  return `${currentContent}${suggestionHtml}`;
}
